<template>
  <b-table-simple
    v-bind="$attrs"
    v-on="$listeners"
    :class="[$attrs.class, 'historical-report-table']"
  >
    <slot></slot>
  </b-table-simple>
</template>

<script>
export default {
  name: 'HistoricalReportTable',
  inheritAttrs: false // Prevents attributes from being applied automatically to the root element
}
</script>

<style scoped lang="scss">
$border-radius: 10px;
$cell-sides-padding: 10px;
$thead-border-width: 3px;

.historical-report-table {
  ::v-deep table {
    border-collapse: separate;
    border-spacing: 0;
  }

  ::v-deep table.b-table > thead > tr > th { // Have to increase selector specificity to override default styles specified in _table.scss
    padding: 7px $cell-sides-padding;
  }

  ::v-deep th {
    position: relative;
    background-color: $bgColorBase;
    text-transform: capitalize;
    font-size: 13px;
    line-height: 16px;
    color: $tableTextHeadColor2;
    text-align: right;
    vertical-align: middle;
    padding: 4px 8px !important;
    white-space: nowrap;

    &.border-right-0 {
      &::after {
        display: none;
      }
    }

    &.border-both-0 {
      &::after {
        display: none;
      }
    }
  }

  ::v-deep td {
    position: relative;
    text-align: right;
    background-color: #fff;
    padding: 12px 4px;
    line-height: 24px;

    &:first-child {
      padding-left: 20px;
    }
  }

  ::v-deep tbody tr td:nth-last-child(2)::after {
    display: none;
  }
  ::v-deep tr:not(.all-time-row, .group-border):hover td {
    background-color: $tableTrHoverColor;
  }

  ::v-deep thead {
    &.sticky-head {
      position: sticky;
      z-index: 1;
    }

    tr th {
      &::after {
        content: "";
        display: block;
        width: 1px;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        background-color: $tableBorderColor;
      }
    }

    tr:last-child th::after {
      top: 0;
    }

    tr:first-child {
      th {
        border-top: $thead-border-width solid #fff;

        // &::after {
        //   top: 4px;
        // }
      }

      th:not([rowspan])::before,
      th[colspan]:not([rowspan])::before {
        content: "";
        display: block;
        height: 1px;
        position: absolute;
        right: 4px;
        bottom: 0;
        left: 4px;
        background-color: $tableBorderColor;
      }

      th:first-child {
        border-left: $thead-border-width solid #fff;
        border-top-left-radius: $border-radius;
      }

      th:last-child {
        border-top-right-radius: $border-radius;
      }
    }

    th:last-child {
      border-right: $thead-border-width solid #fff;

      &::after {
        display: none;
      }
    }

    th:last-child {
      border-right: $thead-border-width solid #fff;
    }
  }

  ::v-deep .empty-row {
    height: 20px;
    background-color: $bgColorBase;
  }

  ::v-deep .all-time-row {
    font-weight: 700;

    .border-right-0::after {
      display: none;
    }

    .border-both-0::after {
      display: none;
    }

    td {
      &::after {
        content: "";
        display: block;
        width: 1px;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 5px;
        background-color: $tableBorderColor;
      }

      &:nth-last-child(2)::after {
        display: none;
      }

      &:first-child {
        border-bottom-left-radius: $border-radius;
        padding-left: $thead-border-width + $cell-sides-padding;
      }

      &:last-child {
        border-bottom-right-radius: $border-radius;
        border-right: 0;

        &::after {
          display: none;
        }
      }
    }
  }

  ::v-deep .group-border {
    font-weight: 700;

    td:first-child {
      border-top-left-radius: $border-radius;
    }

    td:last-child {
      border-top-right-radius: $border-radius;
    }
  }

  ::v-deep .group-border:not(.expanded-year) {
    td:first-child {
      border-bottom-left-radius: $border-radius;
    }

    td:last-child {
      border-bottom-right-radius: $border-radius;
    }
  }

  ::v-deep .expanded-year {
    td {
      border-bottom: 1px solid $tableBorderColor;
    }
  }

  ::v-deep .month-row {
    td {
      &::after {
        content: "";
        display: block;
        width: 1px;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        background-color: $tableBorderColor;
      }

      &::before {
        content: "";
        display: block;
        height: 1px;
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: $tableBorderColor;
      }

      &:first-child::before {
        left: 4px;
      }

      &:last-child::before {
        right: 4px;
      }

      &:last-child::after {
        display: none;
      }
    }

    // &.month-row-first-in-group {
    //   td::after {
    //     top: 12px;
    //   }
    // }

    &.month-row-last-in-group {
      td::before {
        display: none;
      }

      // td::after {
      //   bottom: 12px;
      // }

      td:first-child {
        border-bottom-left-radius: $border-radius;
      }

      td:last-child {
        border-bottom-right-radius: $border-radius;
      }
    }

    .border-right-0 {
      &::after {
        display: none;
      }
    }

    .border-both-0 {
      &::after {
        display: none;
      }
    }
  }

  ::v-deep .arrow-icon {
    width: 20px;
    cursor: pointer;

    span {
      display: block;
      margin: -2px 0;
    }
  }

  ::v-deep tr {
    th, td {
      &.date-column {
        min-width: 80px;
        text-align: left;
      }
    }
  }
}
</style>
